import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `feedback` — the row under a finished answer (PRD-18 AGM-120/121).
 *
 * Moved here from `components/chat/feedback-strings.ts`. A sibling of `chat` rather than
 * part of it: that namespace is about what the machine tells the reader, this one is about
 * what the reader tells the owner.
 *
 * ── Two promises, and both are load-bearing ──
 *
 * `noteHint` and `shareHint` are the only sentences in this product a person reads BEFORE
 * deciding whether to be frank, and both are stated exactly as the system behaves. The owner
 * receives the note WITHOUT a name, and an administrator can see who wrote it. Saying only
 * the first half would be a privacy promise the platform does not keep, and the person
 * deciding whether to write something candid deserves the whole sentence before they type
 * it, not after.
 *
 * `shareHint` does the same for the share offer: the answer, the rest of the conversation
 * and the reader's name all stay here — only the question the reader chooses to send, and
 * edits before sending, leaves. Every clause of both is a fact about the system. A
 * translation may reorder them; it may not drop one, soften one, or add a reassurance the
 * English does not make.
 *
 * English is the source of truth AND the type (LOC-011). Anything that interpolates is a
 * FUNCTION returning a whole sentence (LOC-013).
 */

/** See the note in `chat.ts`: structural, so this file does not close a circle on `I18n`. */
type Counting = { locale: Locale }

const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const feedback = {
  /**
   * The memory-influence disclosure (AGM-092). It is a link, not a note: being told an
   * answer was shaped by saved preferences is only useful next to the ability to go and see
   * — and change — which ones.
   */
  memoryInfluenced: 'Shaped by your saved preferences',
  memoryInfluencedLabel: 'Shaped by your saved preferences — see the preferences this agent uses',

  helpful: 'Helpful',
  unhelpful: 'Not helpful',
  helpfulLabel: 'Mark this answer helpful',
  unhelpfulLabel: 'Mark this answer not helpful',
  recorded: 'Thanks — this reaches the person who owns this agent.',
  voteFailed: 'That could not be saved. Try again.',

  addNote: 'Add a note',
  noteLabel: 'What was wrong, or what worked — optional',
  notePlaceholder: 'It missed the part about last quarter.',
  /** Privacy promise. Both halves, always. See the header. */
  noteHint: 'The owner sees this without your name. Administrators can see who wrote it.',
  /**
   * The countdown beside the note box. It said "1 characters left" on the last keystroke
   * before the cap, every time.
   *
   * `pluralCategory` rather than `left === 1`, because Brazilian Portuguese puts ZERO in
   * the `one` category, so the two-branch check is wrong in one shipped language at the
   * moment the box is full (PRD-27 D-19). The number itself is deliberately NOT grouped:
   * a countdown reads as a countdown, and "1,000" reads as a quantity of something.
   */
  noteRemaining: (fmt: Counting, left: number): string =>
    plural(fmt, left, { one: `${left} character left`, other: `${left} characters left` }),
  noteSend: 'Send note',
  noteSending: 'Sending…',
  noteSent: 'Note sent.',
  noteFailed: 'The note could not be sent. Nothing was lost — try again.',
  noteCancel: 'Cancel',

  shareOffer: 'Share this question with the agent’s owner',
  shareTitle: 'Send your question to the owner',
  /** Privacy promise. Four clauses: what leaves, and the three things that do not. */
  shareHint:
    'They see the question you send here — not the answer, not the rest of this chat, and not your name. Edit it before sending.',
  shareLabel: 'The question to send',
  shareSend: 'Send question',
  shareSending: 'Sending…',
  shareSent: 'Sent. The owner can use it to improve this agent.',
  shareFailed: 'That could not be sent. Nothing was lost — try again.',
  shareCancel: 'Not now'
}
